import { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { Edit, FileText, Image as ImageIcon, LoaderCircle, Plus, Trash2, Upload, Video, X } from 'lucide-react'
import ReactPlayer from 'react-player'
import type { Technique } from '../types/Technique'
import Modal from './Modal'
import ConfirmDialog from './ConfirmDialog'

type MediaKind = 'image' | 'video' | 'pdf'
type MediaPathField = 'imagePath' | 'videoPath' | 'pdfPath'
type UploadState = Record<MediaKind, boolean>
type UploadProgress = Record<MediaKind, number>

interface TechniqueFormData {
  name: string
  description: string
  type: string
  beltLevel: string
  imagePath?: string | null
  videoPath?: string | null
  pdfPath?: string | null
}

type MediaPreviews = Record<'imageUrl' | 'videoUrl' | 'pdfUrl', string | null>

const emptyForm = (): TechniqueFormData => ({
  name: '',
  description: '',
  type: '',
  beltLevel: '',
})

const emptyPreviews = (): MediaPreviews => ({ imageUrl: null, videoUrl: null, pdfUrl: null })
const emptyUploadState = (): UploadState => ({ image: false, video: false, pdf: false })
const emptyUploadProgress = (): UploadProgress => ({ image: 0, video: 0, pdf: 0 })

const mediaConfig: Record<MediaKind, {
  endpoint: string
  formField: string
  pathField: MediaPathField
  previewField: keyof MediaPreviews
  accept: string
  label: string
}> = {
  image: {
    endpoint: '/api/upload/technique-image',
    formField: 'techniqueImage',
    pathField: 'imagePath',
    previewField: 'imageUrl',
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    label: 'Photo',
  },
  video: {
    endpoint: '/api/upload/technique-video',
    formField: 'techniqueVideo',
    pathField: 'videoPath',
    previewField: 'videoUrl',
    accept: 'video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska',
    label: 'Vidéo',
  },
  pdf: {
    endpoint: '/api/upload/technique-pdf',
    formField: 'techniquePdf',
    pathField: 'pdfPath',
    previewField: 'pdfUrl',
    accept: 'application/pdf',
    label: 'PDF',
  },
}

const beltLevels = [
  { value: 'BLANCHE', label: 'Blanche' },
  { value: 'JAUNE', label: 'Jaune' },
  { value: 'ORANGE', label: 'Orange' },
  { value: 'VERTE', label: 'Verte' },
  { value: 'BLEUE', label: 'Bleue' },
  { value: 'MARRON', label: 'Marron' },
  { value: 'NOIRE', label: 'Noire' },
]

const techniqueTypes = [
  { value: 'KIHON', label: 'Kihon' },
  { value: 'KATA', label: 'Kata' },
  { value: 'SELF_DEFENSE', label: 'Self-défense' },
  { value: 'BASES', label: 'Bases' },
  { value: 'CHUTES', label: 'Chutes' },
  { value: 'AUTRE', label: 'Autre' },
]

const revokeBlobUrl = (url: string | null | undefined) => {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url)
}

export default function TechniquesAdminModule() {
  const [techniques, setTechniques] = useState<Technique[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTechnique, setEditingTechnique] = useState<Technique | null>(null)
  const [formData, setFormData] = useState<TechniqueFormData>(emptyForm)
  const [previews, setPreviews] = useState<MediaPreviews>(emptyPreviews)
  const [uploading, setUploading] = useState<UploadState>(emptyUploadState)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(emptyUploadProgress)
  const [saving, setSaving] = useState(false)
  const [techniqueToDelete, setTechniqueToDelete] = useState<Technique | null>(null)
  const [deleting, setDeleting] = useState(false)
  const hasActiveUploads = Object.values(uploading).some(Boolean)

  const fetchTechniques = async () => {
    try {
      setLoading(true)
      const response = await axios.get<Technique[]>('/api/techniques')
      setTechniques(response.data)
    } catch {
      toast.error('Erreur lors du chargement des techniques')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchTechniques()
  }, [])

  const deleteOrphan = async (path: string) => {
    try {
      await axios.delete('/api/upload', { data: { path } })
    } catch (error) {
      if (!axios.isAxiosError(error) || ![404, 409].includes(error.response?.status ?? 0)) {
        console.error(`Impossible de nettoyer le fichier temporaire ${path} :`, error)
      }
    }
  }

  const clearFormState = () => {
    Object.values(previews).forEach(revokeBlobUrl)
    setFormData(emptyForm())
    setPreviews(emptyPreviews())
    setEditingTechnique(null)
  }

  const cleanupPendingUploads = async () => {
    const pendingPaths = [formData.imagePath, formData.videoPath, formData.pdfPath]
      .filter((value): value is string => typeof value === 'string')
    await Promise.all(pendingPaths.map(deleteOrphan))
  }

  const openCreate = () => {
    clearFormState()
    setShowForm(true)
  }

  const openEdit = (technique: Technique) => {
    clearFormState()
    setEditingTechnique(technique)
    setFormData({
      name: technique.name,
      description: technique.description,
      type: technique.type,
      beltLevel: technique.beltLevel,
    })
    setPreviews(technique.media)
    setShowForm(true)
  }

  const closeForm = async () => {
    if (saving || hasActiveUploads) return
    await cleanupPendingUploads()
    setShowForm(false)
    clearFormState()
  }

  const handleUpload = async (file: File, kind: MediaKind) => {
    const config = mediaConfig[kind]
    const payload = new FormData()
    payload.append(config.formField, file)
    setUploading((current) => ({ ...current, [kind]: true }))
    setUploadProgress((current) => ({ ...current, [kind]: 0 }))

    try {
      const response = await axios.post<{ path: string }>(config.endpoint, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          const progress = event.total
            ? Math.round((event.loaded / event.total) * 100)
            : Math.round((event.progress ?? 0) * 100)
          setUploadProgress((current) => ({
            ...current,
            [kind]: Math.min(100, Math.max(0, progress)),
          }))
        },
      })
      const previousPendingPath = formData[config.pathField]
      if (typeof previousPendingPath === 'string') await deleteOrphan(previousPendingPath)

      revokeBlobUrl(previews[config.previewField])
      const previewUrl = URL.createObjectURL(file)
      setFormData((current) => ({ ...current, [config.pathField]: response.data.path }))
      setPreviews((current) => ({ ...current, [config.previewField]: previewUrl }))
      toast.success(`${config.label} ${kind === 'pdf' ? 'ajouté' : 'ajoutée'} localement`)
    } catch (error) {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : null
      toast.error(message || `Impossible d’envoyer le fichier ${config.label.toLowerCase()}`)
    } finally {
      setUploading((current) => ({ ...current, [kind]: false }))
    }
  }

  const removeMedia = async (kind: MediaKind) => {
    const config = mediaConfig[kind]
    const pendingPath = formData[config.pathField]
    if (typeof pendingPath === 'string') await deleteOrphan(pendingPath)
    revokeBlobUrl(previews[config.previewField])
    setFormData((current) => ({ ...current, [config.pathField]: null }))
    setPreviews((current) => ({ ...current, [config.previewField]: null }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (hasActiveUploads) return
    setSaving(true)
    try {
      if (editingTechnique) {
        await axios.put(`/api/techniques/${editingTechnique.id}`, formData)
        toast.success('Technique modifiée')
      } else {
        await axios.post('/api/techniques', formData)
        toast.success('Technique créée')
      }
      setShowForm(false)
      clearFormState()
      await fetchTechniques()
    } catch (error) {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : null
      toast.error(message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!techniqueToDelete) return
    setDeleting(true)
    try {
      await axios.delete(`/api/techniques/${techniqueToDelete.id}`)
      toast.success('Technique supprimée')
      setTechniqueToDelete(null)
      await fetchTechniques()
    } catch {
      toast.error('Erreur lors de la suppression')
    } finally {
      setDeleting(false)
    }
  }

  const renderUploadButton = (kind: MediaKind) => {
    const config = mediaConfig[kind]
    if (uploading[kind]) {
      const progress = uploadProgress[kind]
      return (
        <div
          className="min-w-40 space-y-1"
          role="progressbar"
          aria-label={`Envoi de ${config.label.toLowerCase()}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div className="flex items-center justify-between gap-3 text-xs font-semibold text-gray-700">
            <span>Envoi en cours…</span>
            <span>{progress} %</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-primary-600 transition-[width] duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )
    }

    return (
      <label className="btn-secondary cursor-pointer">
        <Upload className="h-4 w-4" />
        {previews[config.previewField] ? 'Remplacer' : 'Choisir un fichier'}
        <input
          type="file"
          accept={config.accept}
          className="hidden"
          disabled={uploading[kind] || saving}
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void handleUpload(file, kind)
            event.target.value = ''
          }}
        />
      </label>
    )
  }

  return (
    <div className="space-y-6">
      <div className="admin-module-toolbar">
        <div><p className="eyebrow !mb-1">Pédagogie</p><h3 className="text-xl font-extrabold text-gray-900">Techniques existantes</h3></div>
        <button type="button" onClick={openCreate} className="btn-primary"><Plus size={20} /> Nouvelle technique</button>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => void closeForm()}
        title={editingTechnique ? 'Modifier la technique' : 'Nouvelle technique'}
        eyebrow="Bibliothèque pédagogique"
        size="lg"
        footer={(
          <>
            <button type="button" onClick={() => void closeForm()} className="btn-secondary" disabled={saving || hasActiveUploads}>Annuler</button>
            <button type="submit" form="technique-admin-form" className="btn-primary" disabled={saving || hasActiveUploads}>
              {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}{editingTechnique ? 'Mettre à jour' : 'Créer'}
            </button>
          </>
        )}
      >
        <form id="technique-admin-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="form-field">
            <label className="form-label" htmlFor="technique-name">Nom *</label>
            <input id="technique-name" type="text" required minLength={3} placeholder="Ex. Défense sur saisie de poignet" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} className="input-field" />
          </div>
          <div className="form-field">
            <label className="form-label" htmlFor="technique-description">Description *</label>
            <textarea id="technique-description" required minLength={10} placeholder="Décrivez les étapes, les points de vigilance et le résultat attendu…" value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} className="input-field" />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="form-field" htmlFor="technique-type"><span className="form-label">Type *</span>
              <select id="technique-type" required value={formData.type} onChange={(event) => setFormData({ ...formData, type: event.target.value })} className="input-field">
                <option value="">Sélectionner un type</option>
                {techniqueTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
            </label>
            <label className="form-field" htmlFor="technique-belt"><span className="form-label">Niveau de ceinture *</span>
              <select id="technique-belt" required value={formData.beltLevel} onChange={(event) => setFormData({ ...formData, beltLevel: event.target.value })} className="input-field">
                <option value="">Sélectionner un niveau</option>
                {beltLevels.map((level) => <option key={level.value} value={level.value}>{level.label}</option>)}
              </select>
            </label>
          </div>

          <section className="technique-supports">
            <div className="form-section-heading"><div><p className="eyebrow !mb-1">Fichiers locaux</p><h3>Supports pédagogiques</h3><p>Ajoutez les ressources qui accompagneront la fiche de la technique.</p></div></div>
            <div className="technique-support-grid">
              <article className="technique-support-card">
                <div className="technique-support-heading"><span aria-hidden="true"><ImageIcon /></span><div><strong>Photo</strong><small>Repère visuel</small></div></div>
                <div className="technique-support-preview">
                  {previews.imageUrl ? <img src={previews.imageUrl} alt="Aperçu de la technique" /> : <span><ImageIcon aria-hidden="true" />Aucune photo</span>}
                </div>
                <div className="technique-support-actions">{renderUploadButton('image')}{previews.imageUrl && <button type="button" className="icon-button icon-button-danger" onClick={() => void removeMedia('image')} aria-label="Retirer la photo"><X /></button>}</div>
                <p className="technique-support-note">JPG, PNG, GIF ou WebP · 5 Mo max.</p>
              </article>

              <article className="technique-support-card">
                <div className="technique-support-heading"><span aria-hidden="true"><Video /></span><div><strong>Vidéo</strong><small>Démonstration</small></div></div>
                <div className="technique-support-preview technique-support-video">
                  {previews.videoUrl ? <ReactPlayer src={previews.videoUrl} controls width="100%" height="144px" /> : <span><Video aria-hidden="true" />Aucune vidéo</span>}
                </div>
                <div className="technique-support-actions">{renderUploadButton('video')}{previews.videoUrl && <button type="button" className="icon-button icon-button-danger" onClick={() => void removeMedia('video')} aria-label="Retirer la vidéo"><X /></button>}</div>
                <p className="technique-support-note">MP4, WebM, OGG, MOV ou MKV · 1 Go max.</p>
              </article>

              <article className="technique-support-card">
                <div className="technique-support-heading"><span aria-hidden="true"><FileText /></span><div><strong>PDF</strong><small>Fiche détaillée</small></div></div>
                <div className="technique-support-preview">
                  {previews.pdfUrl ? <a href={previews.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary"><FileText className="h-4 w-4" /> Voir le PDF</a> : <span><FileText aria-hidden="true" />Aucun PDF</span>}
                </div>
                <div className="technique-support-actions">{renderUploadButton('pdf')}{previews.pdfUrl && <button type="button" className="icon-button icon-button-danger" onClick={() => void removeMedia('pdf')} aria-label="Retirer le PDF"><X /></button>}</div>
                <p className="technique-support-note">PDF uniquement · 20 Mo max.</p>
              </article>
            </div>
          </section>

        </form>
      </Modal>

      <div className="card mt-6 !overflow-hidden !p-0">
        {loading ? (
          <div className="flex justify-center py-8"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" /></div>
        ) : techniques.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune technique pour le moment.</div>
        ) : (
          <div className="data-table-wrap"><table className="admin-table">
            <thead><tr><th>Nom</th><th>Type</th><th>Ceinture</th><th>Photo</th><th>Vidéo</th><th>PDF</th><th /></tr></thead>
            <tbody>{techniques.map((technique) => (
              <tr key={technique.id}>
                <td className="font-medium">{technique.name}</td>
                <td>{techniqueTypes.find((type) => type.value === technique.type)?.label || technique.type}</td>
                <td>{beltLevels.find((belt) => belt.value === technique.beltLevel)?.label || technique.beltLevel}</td>
                <td>{technique.media.imageUrl ? <img src={technique.media.imageUrl} alt="" className="h-12 w-16 rounded-lg object-cover" /> : <span className="text-gray-400">—</span>}</td>
                <td>{technique.media.videoUrl ? <a href={technique.media.videoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 underline"><Video className="h-4 w-4" /> Voir</a> : <span className="text-gray-400">—</span>}</td>
                <td>{technique.media.pdfUrl ? <a href={technique.media.pdfUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 underline"><FileText className="h-4 w-4" /> PDF</a> : <span className="text-gray-400">—</span>}</td>
                <td><div className="flex gap-2"><button type="button" onClick={() => openEdit(technique)} className="icon-button" title="Modifier" aria-label={`Modifier ${technique.name}`}><Edit /></button><button type="button" onClick={() => setTechniqueToDelete(technique)} className="icon-button icon-button-danger" title="Supprimer" aria-label={`Supprimer ${technique.name}`}><Trash2 /></button></div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      <ConfirmDialog isOpen={techniqueToDelete !== null} title="Supprimer la technique" message={`La technique « ${techniqueToDelete?.name ?? ''} » et ses médias seront définitivement supprimés.`} confirmLabel="Supprimer" isBusy={deleting} onConfirm={handleDelete} onCancel={() => setTechniqueToDelete(null)} />
    </div>
  )
}
